import { closedDayReason, closedDaysLabel } from "@car/api/catalog-config";
import { tehranDay } from "@car/api/service-config";
import { Button } from "@car/ui/components/button";
import { Calendar } from "@car/ui/components/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@car/ui/components/card";
import { FormAlert, statusControlClass, useAppForm, useStore } from "@car/ui/components/form";
import { Skeleton } from "@car/ui/components/skeleton";
import { optionalText, requiredChoice } from "@car/ui/lib/form-rules";
import { cn, SEP } from "@car/ui/lib/utils";
import { ArrowRight01Icon, Car03Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import {
  NoVehicleEmpty,
  PageHeader,
  PanelEmpty,
  PanelQueryError,
  Plate,
} from "@/components/panel-bits";
import { faDate, faPlainNumber } from "@/lib/format";
import {
  isOpenAppointment,
  serviceTypeKeys,
  serviceTypeLabels,
  type Appointment,
  type Branch,
  type GarageVehicle,
  type ServiceTypeKey,
} from "@/lib/panel";
import { orpc } from "@/utils/orpc";

const newAppointmentSearchSchema = z.object({
  vehicle: z.string().optional().catch(undefined),
  /** an open booking of the customer to move instead of booking anew */
  reschedule: z.string().optional().catch(undefined),
});

const DAY_MS = 86_400_000;
/** How far ahead the calendar greys out holidays (booking further out is still checked by the server) */
const CALENDAR_DAYS = 365;

const isServiceTypeKey = (value: string): value is ServiceTypeKey =>
  serviceTypeKeys.some((key) => key === value);

export const Route = createFileRoute("/panel/appointments/new")({
  validateSearch: newAppointmentSearchSchema,
  head: () => ({ meta: [{ title: "رزرو یا تغییر زمان نوبت | پنل مشتریان تابان خودرو" }] }),
  component: NewAppointmentPage,
});

function NewAppointmentPage() {
  const { vehicle: preselected, reschedule } = Route.useSearch();
  const garageQuery = useQuery(orpc.panel.garage.list.queryOptions());
  const branchesQuery = useQuery(orpc.panel.appointments.branches.queryOptions());
  const bookingsQuery = useQuery(
    orpc.panel.appointments.list.queryOptions({ enabled: Boolean(reschedule) }),
  );
  // فقط نوبت باز «رزرو شده» خود مشتری جابه‌جا می‌شود؛ سرور هم همین را بررسی می‌کند
  const moving = reschedule
    ? bookingsQuery.data?.appointments.find(
        (item) =>
          item.id === reschedule &&
          item.status === "scheduled" &&
          isOpenAppointment(item.status, item.dateIso),
      )
    : undefined;
  const loading =
    garageQuery.isPending ||
    branchesQuery.isPending ||
    (Boolean(reschedule) && bookingsQuery.isPending);
  const bookable = [
    ...(garageQuery.data?.garage ?? []),
    // خودروی مشترکی که مالک اجازه رزرو برایش داده
    ...(garageQuery.data?.shared ?? []).filter((item) => item.access.canBook),
  ];

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={reschedule ? "تغییر زمان نوبت" : "رزرو نوبت جدید"}
        description={
          reschedule
            ? "روز، ساعت یا شعبه تازه را انتخاب کنید؛ نوبت فعلی تا ثبت زمان تازه برای شما می‌ماند."
            : "خودرو، شعبه، نوع خدمت و زمان مراجعه را انتخاب کنید."
        }
        action={
          <Button variant="ghost" nativeButton={false} render={<Link to="/panel/appointments" />}>
            <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={1.8} />
            بازگشت به رزروها
          </Button>
        }
      />
      {/* مقدار پیش‌فرض خودرو و شعبه از داده می‌آید؛ فرم بعد از رسیدن هر دو ساخته می‌شود */}
      {loading ? (
        <div className="grid gap-6">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      ) : garageQuery.isError || branchesQuery.isError || bookingsQuery.isError ? (
        <PanelQueryError
          title="فرم رزرو آماده نشد"
          query={
            garageQuery.isError
              ? garageQuery
              : branchesQuery.isError
                ? branchesQuery
                : bookingsQuery
          }
        />
      ) : reschedule && !moving ? (
        <PanelEmpty
          icon={Car03Icon}
          title="این نوبت دیگر قابل تغییر نیست"
          description="نوبت لغو یا پذیرش شده یا زمانش گذشته است؛ می‌توانید نوبت تازه بگیرید."
          action={
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to="/panel/appointments" />}
            >
              بازگشت به رزروها
            </Button>
          }
        />
      ) : bookable.length === 0 ? (
        // بی‌خودرو، همه‌ی فیلدهای فرم بی‌فایده است و فقط با خطای تاریخ رد می‌شد؛ راه ادامه
        // (درخواست افزودن خودرو به پرونده) همان است که صفحه‌ی «خودروهای من» می‌دهد
        <NoVehicleEmpty />
      ) : (
        <AppointmentForm
          garage={bookable}
          branches={branchesQuery.data?.branches ?? []}
          preselected={moving?.ownedVehicleId ?? preselected}
          moving={moving}
        />
      )}
    </div>
  );
}

const appointmentSchema = z.object({
  vehicleId: requiredChoice("خودرو"),
  branchId: requiredChoice("شعبه"),
  serviceType: requiredChoice("نوع خدمت"),
  dateIso: requiredChoice("روز مراجعه"),
  slot: requiredChoice("ساعت مراجعه"),
  note: optionalText("توضیحات", 500),
});

/** شعبه تعیین می‌کند چه خدمتی رزروشدنی است؛ خدمت انتخابی اگر در شعبه نباشد اولین خدمت آن می‌شود */
const serviceAt = (branch: Branch | undefined, current: string) => {
  const offered = branch?.services ?? [];
  return offered.length === 0 || offered.some((item) => item.key === current)
    ? current
    : (offered[0]?.key ?? current);
};

function AppointmentForm({
  garage,
  branches,
  preselected,
  moving,
}: {
  garage: GarageVehicle[];
  branches: Branch[];
  preselected: string | undefined;
  /** the open booking being moved; the vehicle stays, everything else may change */
  moving?: Appointment;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState("");

  const createMutation = useMutation(orpc.panel.appointments.create.mutationOptions());
  const rescheduleMutation = useMutation(orpc.panel.appointments.reschedule.mutationOptions());
  const startBranch = branches.find((item) => item.id === moving?.branchId) ?? branches[0];
  // خودرویی که نوبتش جابه‌جا می‌شود عوض نمی‌شود
  const vehicles = moving
    ? garage.filter((item) => item.owned.id === moving.ownedVehicleId)
    : garage;

  const form = useAppForm({
    defaultValues: {
      vehicleId: preselected ?? garage[0]?.owned.id ?? "",
      branchId: startBranch?.id ?? "",
      serviceType: serviceAt(startBranch, moving?.serviceType ?? "periodic"),
      dateIso: "",
      slot: "",
      note: moving?.note ?? "",
    },
    validators: { onBlur: appointmentSchema },
    onSubmit: async ({ value, formApi }) => {
      if (!isServiceTypeKey(value.serviceType)) return;
      setError("");
      const booking = {
        branchId: value.branchId,
        dateIso: value.dateIso,
        slot: value.slot,
        serviceType: value.serviceType,
        note: value.note.trim(),
      };
      const result = await (
        moving
          ? rescheduleMutation.mutateAsync({ ...booking, id: moving.id })
          : createMutation.mutateAsync({ ...booking, ownedVehicleId: value.vehicleId })
      ).catch(() => null);
      if (!result) {
        setError("ثبت انجام نشد؛ اتصال را بررسی و دوباره تلاش کنید.");
        return;
      }
      if (!result.ok) {
        // ساعت احتمالاً همین حالا پر شده؛ انتخاب پاک و فهرست ساعت‌ها تازه می‌شود
        setError(result.reason);
        formApi.setFieldValue("slot", "");
        void queryClient.invalidateQueries({ queryKey: orpc.panel.appointments.slots.key() });
        return;
      }
      toast.success(moving ? "زمان نوبت شما تغییر کرد" : "نوبت شما با موفقیت ثبت شد");
      queryClient.invalidateQueries();
      navigate({ to: "/panel/appointments" });
    },
  });

  const values = useStore(form.store, (state) => state.values);
  const branch = branches.find((item) => item.id === values.branchId);
  const offered = branch?.services ?? [];
  const slotsQuery = useQuery(
    orpc.panel.appointments.slots.queryOptions({
      // جای خود نوبتی که جابه‌جا می‌شود آزاد حساب می‌شود
      input: { branchId: values.branchId, dateIso: values.dateIso, exceptId: moving?.id ?? "" },
      enabled: Boolean(values.dateIso && values.branchId),
    }),
  );
  const complete = Boolean(
    values.vehicleId && values.branchId && values.serviceType && values.dateIso && values.slot,
  );

  // روزها به وقت تهران: تقویم با timeZone تهران ساخته می‌شود و قاعده همان قاعده سرور است
  const today = tehranDay(new Date());
  const calendarQuery = useQuery(
    orpc.panel.appointments.calendar.queryOptions({
      input: {
        branchId: values.branchId,
        from: today,
        to: tehranDay(new Date(Date.now() + CALENDAR_DAYS * DAY_MS)),
      },
      enabled: Boolean(values.branchId),
      staleTime: 5 * 60_000,
    }),
  );
  const closedWeekdays = calendarQuery.data?.closedWeekdays ?? branch?.closedWeekdays ?? [];
  const holidays = calendarQuery.data?.holidays ?? [];
  const closedOn = (day: Date) =>
    closedDayReason({ name: branch?.name ?? "", closedWeekdays }, tehranDay(day), holidays) !==
    null;
  const weeklyClosed = closedDaysLabel(closedWeekdays);

  return (
    <form.AppForm>
      <form.Form className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">۱. انتخاب خودرو</CardTitle>
            {moving ? (
              <p className="text-sm text-muted-foreground">
                نوبت فعلی: {serviceTypeLabels[moving.serviceType]}، {faDate(moving.dateIso)} ساعت{" "}
                {moving.slot}
                {SEP}
                {moving.branchName}
              </p>
            ) : null}
          </CardHeader>
          <CardContent>
            <form.AppField name="vehicleId">
              {(field) => (
                <field.FormField label={<span className="sr-only">خودرو</span>}>
                  {({ controlProps }) =>
                    vehicles.length === 0 ? (
                      <div id={controlProps.id}>
                        <PanelEmpty
                          icon={Car03Icon}
                          title="خودرویی به نام شما ثبت نشده"
                          description="برای رزرو نوبت باید خودرویی در پرونده شما باشد؛ با شعبه تماس بگیرید."
                        />
                      </div>
                    ) : (
                      <div
                        id={controlProps.id}
                        aria-invalid={controlProps["aria-invalid"]}
                        aria-describedby={controlProps["aria-describedby"]}
                        className="grid gap-3 sm:grid-cols-2"
                        role="radiogroup"
                        aria-label="انتخاب خودرو"
                      >
                        {vehicles.map((item) => {
                          const selected = field.state.value === item.owned.id;
                          return (
                            <button
                              key={item.owned.id}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              onClick={() => {
                                field.handleChange(item.owned.id);
                                field.handleBlur();
                              }}
                              className={cn(
                                "grid gap-2 rounded-xl border p-4 text-start transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                selected ? "border-primary bg-primary/5" : "hover:bg-muted/60",
                              )}
                            >
                              <span className="font-bold">
                                {item.vehicle.name}{" "}
                                <span className="font-normal text-muted-foreground">
                                  مدل {faPlainNumber(item.owned.modelYear)}
                                </span>
                              </span>
                              <Plate
                                plate={item.owned.plate}
                                text={item.owned.plateText}
                                className="justify-self-start"
                              />
                            </button>
                          );
                        })}
                      </div>
                    )
                  }
                </field.FormField>
              )}
            </form.AppField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">۲. شعبه و نوع خدمت</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <form.AppField
              name="branchId"
              listeners={{
                // ساعت‌ها و خدمت‌ها مال شعبه‌اند؛ با عوض شدن شعبه ساعت پاک و خدمت هم‌خوان می‌شود
                onChange: ({ value }) => {
                  form.setFieldValue("slot", "");
                  form.setFieldValue(
                    "serviceType",
                    serviceAt(
                      branches.find((item) => item.id === value),
                      form.getFieldValue("serviceType"),
                    ),
                  );
                },
              }}
            >
              {(field) => (
                <field.SelectField
                  label="شعبه"
                  disabled={branches.length === 0}
                  options={branches.map((item) => ({ value: item.id, label: item.name }))}
                  description={
                    branch
                      ? `${branch.address}${branch.hours ? `${SEP}${branch.hours}` : ""}`
                      : null
                  }
                />
              )}
            </form.AppField>
            <form.AppField name="serviceType">
              {(field) => (
                <field.SelectField
                  label="نوع خدمت"
                  disabled={offered.length === 0}
                  options={offered.map((item) => ({ value: item.key, label: item.label }))}
                />
              )}
            </form.AppField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">۳. تاریخ و ساعت مراجعه</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-[auto_1fr]">
            <form.AppField name="dateIso">
              {(field) => (
                <field.FormField label={<span className="sr-only">روز مراجعه</span>}>
                  {({ status, controlProps }) => (
                    <div
                      id={controlProps.id}
                      aria-invalid={controlProps["aria-invalid"]}
                      aria-describedby={controlProps["aria-describedby"]}
                    >
                      <Calendar
                        mode="single"
                        timeZone="Asia/Tehran"
                        selected={field.state.value ? new Date(field.state.value) : undefined}
                        onSelect={(next) => {
                          // نیمه‌شب تهران؛ TZDate.toISOString اختلاف ساعت می‌نویسد و سرور Z می‌خواهد
                          field.handleChange(next ? new Date(next.getTime()).toISOString() : "");
                          field.handleBlur();
                          form.setFieldValue("slot", "");
                          setError("");
                        }}
                        disabled={(day) => tehranDay(day) < today || closedOn(day)}
                        className={cn(
                          "rounded-xl border",
                          statusControlClass(status),
                          status === "invalid" && "border-destructive",
                        )}
                      />
                    </div>
                  )}
                </field.FormField>
              )}
            </form.AppField>
            <form.AppField name="slot">
              {(field) => (
                <field.FormField
                  className="content-start"
                  label={
                    values.dateIso
                      ? `ساعت‌های خالی ${faDate(values.dateIso)}`
                      : "ابتدا یک روز را انتخاب کنید"
                  }
                >
                  {({ controlProps }) =>
                    !values.dateIso ? (
                      <p id={controlProps.id} className="text-sm text-muted-foreground">
                        {weeklyClosed
                          ? `این شعبه ${weeklyClosed} و در تعطیلات رسمی تعطیل است`
                          : "این شعبه همه روزهای هفته جز تعطیلات رسمی باز است"}
                        ؛ روزهای تعطیل در تقویم غیرفعال‌اند. ظرفیت روزانه شعبه میان ساعت‌های مراجعه
                        پخش می‌شود و جای خالی هر ساعت کنار آن نوشته شده است.
                      </p>
                    ) : slotsQuery.isPending ? (
                      <div id={controlProps.id} className="flex flex-wrap gap-2">
                        {[0, 1, 2, 3, 4].map((index) => (
                          <Skeleton key={index} className="h-9 w-20 rounded-md" />
                        ))}
                      </div>
                    ) : (
                      <>
                        <div
                          id={controlProps.id}
                          aria-invalid={controlProps["aria-invalid"]}
                          aria-describedby={controlProps["aria-describedby"]}
                          className="flex flex-wrap gap-2"
                          role="radiogroup"
                          aria-label="انتخاب ساعت"
                        >
                          {(slotsQuery.data?.slots ?? []).map((item) => (
                            <Button
                              key={item.slot}
                              type="button"
                              role="radio"
                              aria-checked={field.state.value === item.slot}
                              variant={field.state.value === item.slot ? "default" : "outline"}
                              disabled={item.taken}
                              onClick={() => {
                                field.handleChange(item.slot);
                                field.handleBlur();
                              }}
                            >
                              {item.slot}
                              <span className="text-xs font-normal opacity-80">
                                {item.taken ? "پر" : `${faPlainNumber(item.left)} جا`}
                              </span>
                            </Button>
                          ))}
                        </div>
                        {slotsQuery.data?.closedReason ? (
                          <p className="text-sm text-destructive">
                            {slotsQuery.data.closedReason} روز دیگری را انتخاب کنید.
                          </p>
                        ) : slotsQuery.data?.slots.every((item) => item.taken) ? (
                          <p className="text-sm text-destructive">
                            همه ساعت‌های این روز پر شده است؛ روز دیگری را انتخاب کنید.
                          </p>
                        ) : null}
                      </>
                    )
                  }
                </field.FormField>
              )}
            </form.AppField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">۴. توضیحات (اختیاری)</CardTitle>
          </CardHeader>
          <CardContent>
            <form.AppField name="note">
              {(field) => (
                <field.TextareaField
                  label={<span className="sr-only">توضیحات برای پذیرش</span>}
                  placeholder="اگر ایراد یا نکته خاصی هست که تیم پذیرش باید بداند، همین‌جا بنویسید."
                  maxLength={500}
                  rows={4}
                />
              )}
            </form.AppField>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {error ? (
            <FormAlert message={error} />
          ) : !complete ? (
            <span className="text-sm text-muted-foreground">
              برای ثبت، خودرو، روز و ساعت را انتخاب کنید.
            </span>
          ) : null}
          <form.SubmitButton
            size="lg"
            pending={createMutation.isPending || rescheduleMutation.isPending}
          >
            {moving ? "ثبت زمان تازه" : "ثبت نهایی نوبت"}
          </form.SubmitButton>
        </div>
      </form.Form>
    </form.AppForm>
  );
}
